// src/components/NavbarComponent.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Home,
  Search,
  User,
  Settings,
  FileText,
  Shield,
  HelpCircle,
  Menu,
  Heart,
  Users,
  LayoutDashboard,
  LogOut,
  ChevronDown,
  X,
  Sun,
  Moon,
  Wrench,
  Flag,
  CheckCircle,
  ChevronRight,
  Info,
  Briefcase,
  Images
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage, languages } from '../contexts/LanguageContext';
import useTranslation from '../hooks/useTranslation';

// Import Redux actions and selectors
import { logout } from '../store/slices/authSlice';
import {
  selectIsAuthenticated,
  selectUser,
  selectUserRole,
  selectIsProvider,
  selectIsAdmin,
  selectAuthLoading,
  selectUserLanguage,
  selectIsGuestMode
} from '../store/slices/authSlice';

import styles from '../styles/NavbarComponent.module.css';

const NavbarComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redux selectors
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const isProvider = useSelector(selectIsProvider);
  const isAdmin = useSelector(selectIsAdmin);
  const isLoading = useSelector(selectAuthLoading);
  const userLanguage = useSelector(selectUserLanguage);
  const isGuestMode = useSelector(selectIsGuestMode);

  const { isDark, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, isTranslating } = useLanguage();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isQuickLinksOpen, setIsQuickLinksOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const mobileMenuRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const quickLinksRef = useRef(null);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: home } = useTranslation('Home');
  const { translatedText: providers } = useTranslation('Providers');
  const { translatedText: profile } = useTranslation('Profile');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: services } = useTranslation('Services');
  const { translatedText: helpSupport } = useTranslation('Help & Support');
  const { translatedText: terms } = useTranslation('Terms of Service');
  const { translatedText: privacy } = useTranslation('Privacy Policy');
  const { translatedText: donation } = useTranslation('Donation');
  const { translatedText: reports } = useTranslation('Reports');
  const { translatedText: gallery } = useTranslation('Gallery');
  const { translatedText: logoutLabel } = useTranslation('Logout');
  const { translatedText: language } = useTranslation('Language');
  const { translatedText: darkMode } = useTranslation('Dark Mode');
  const { translatedText: lightMode } = useTranslation('Light Mode');
  const { translatedText: signIn } = useTranslation('Sign In');
  const { translatedText: register } = useTranslation('Register');
  const { translatedText: haitianServices } = useTranslation('Haitian Services Platform');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: about } = useTranslation('About');

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(e.target)) {
        setIsLanguageDropdownOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (quickLinksRef.current && !quickLinksRef.current.contains(e.target)) {
        setIsQuickLinksOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Handle logout
  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await dispatch(logout());
      navigate('/login');
      setIsMobileMenuOpen(false);
    }
  };

  // Handle language selection
  const handleLanguageSelect = async (languageCode) => {
    if (languageCode !== currentLanguage) {
      await changeLanguage(languageCode);
    }
    setIsLanguageDropdownOpen(false);
  };

  const getCurrentLanguageLabel = () => {
    const lang = languages.find(l => l.code === currentLanguage);
    return lang ? `${lang.flag}` : '🇺🇸';
  };

  const getCurrentLanguageName = () => {
    const lang = languages.find(l => l.code === currentLanguage);
    return lang ? lang.name : 'English';
  };

  // Navigation items based on user role
  const getNavItems = () => {
    if (isAdmin) {
      return [
        { key: 'admin/reports', label: reports, icon: Flag, path: '/admin/reports' },
        { key: 'admin/providers', label: providers, icon: Users, path: '/admin/providers' },
        { key: 'admin/donations', label: donation, icon: Heart, path: '/admin/donations' },
      ];
    }
    
    if (isProvider) {
      return [
        { key: 'provider-dashboard', label: dashboard, icon: LayoutDashboard, path: '/provider-dashboard' },
        { key: 'provider-services', label: services, icon: Wrench, path: '/provider-services' },
        { key: 'provider-gallery', label: gallery, icon: Images, path: '/provider-gallery' },
        { key: 'provider-profile', label: profile, icon: User, path: '/provider-profile' },
        { key: 'provider-reports', label: reports, icon: Flag, path: '/provider-reports' },
      ];
    }
    
    // Client nav items
    return [
      { key: 'home', label: home, icon: Home, path: '/home' },
      { key: 'providers', label: providers, icon: Search, path: '/providers' },
      { key: 'donation', label: donation, icon: Heart, path: '/donation' },
    ];
  };

  const navItems = getNavItems();

  // Quick links for all users
  const quickLinksItems = [
    { key: 'home', label: home, icon: Home, path: '/home' },
    { key: 'providers', label: providers, icon: Search, path: '/providers' },
    { key: 'donation', label: donation, icon: Heart, path: '/donation' },
    { key: 'help-support', label: helpSupport, icon: HelpCircle, path: '/help-support' },
    { key: 'about', label: about, icon: Info, path: '/about' },
  ];

  const legalItems = [
    { key: 'terms', label: terms, icon: FileText, path: '/terms' },
    { key: 'privacy', label: privacy, icon: Shield, path: '/privacy' },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Don't render if loading
  if (isLoading) {
    return (
      <nav className={styles.navComponent}>
        <div className={styles.navContainer}>
          <div className={styles.navBrand}>
            <span className={styles.navBrandText}>{haitianServices}</span>
          </div>
          <div className={styles.loadingIndicator}>Loading...</div>
        </div>
      </nav>
    );
  }

  return (
    <nav className={`${styles.navComponent} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.navContainer}>
        {/* Logo */}
        <div className={styles.navBrand}>
          <Link to={isAuthenticated ? (isAdmin ? '/admin/reports' : isProvider ? '/provider-dashboard' : '/home') : '/'}>
            <img src="/logo/logo_hsm.png" alt={haitianServices} className={styles.navLogo} />
            <span className={styles.navBrandText}>{haitianServices}</span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className={styles.navLinks}>
          {isAuthenticated ? (
            navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`${styles.navLink} ${isActive(item.path) ? styles.active : ''}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })
          ) : (
            <>
              <Link 
                to="/login" 
                className={`${styles.navLink} ${location.pathname === '/login' ? styles.active : ''}`}
              >
                <span>{signIn}</span>
              </Link>
              <Link 
                to="/register" 
                className={`${styles.navLink} ${styles.registerLink} ${location.pathname === '/register' ? styles.active : ''}`}
              >
                <span>{register}</span>
              </Link>
            </>
          )}
        </div>

        {/* Desktop Right Section */}
        <div className={styles.navRight}>
          {/* Quick Links Dropdown */}
          <div className={styles.quickLinksDropdown} ref={quickLinksRef}>
            <button 
              className={styles.quickLinksToggle}
              onClick={() => setIsQuickLinksOpen(!isQuickLinksOpen)}
              aria-label="Quick Links"
            >
              <span>{quickLinks}</span>
              <ChevronDown size={16} className={isQuickLinksOpen ? styles.rotated : ''} />
            </button>
            {isQuickLinksOpen && (
              <div className={`${styles.dropdownMenu} ${styles.quickLinksMenu}`}>
                {quickLinksItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className={`${styles.dropdownItem} ${isActive(item.path) ? styles.active : ''}`}
                      onClick={() => setIsQuickLinksOpen(false)}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                      {isActive(item.path) && <CheckCircle size={16} className={styles.dropdownItemCheck} />}
                    </Link>
                  );
                })}
                <div className={styles.dropdownDivider} />
                <span className={styles.dropdownLabel}>{legal}</span>
                {legalItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className={`${styles.dropdownItem} ${isActive(item.path) ? styles.active : ''}`}
                      onClick={() => setIsQuickLinksOpen(false)}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                      {isActive(item.path) && <CheckCircle size={16} className={styles.dropdownItemCheck} />}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button 
            className={styles.themeToggleBtn}
            onClick={toggleTheme}
            title={isDark ? lightMode : darkMode}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Language Dropdown */}
          <div className={styles.languageDropdown} ref={languageDropdownRef}>
            <button 
              className={styles.languageToggleBtn}
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              disabled={isTranslating}
              aria-label="Change language"
            >
              <span className={styles.languageFlag}>{getCurrentLanguageLabel()}</span>
              <span className={styles.languageName}>{getCurrentLanguageName()}</span>
              <ChevronDown size={16} className={isLanguageDropdownOpen ? styles.rotated : ''} />
            </button>
            {isLanguageDropdownOpen && (
              <div className={`${styles.dropdownMenu} ${styles.languageMenu}`}>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    className={`${styles.dropdownItem} ${currentLanguage === lang.code ? styles.active : ''}`}
                    onClick={() => handleLanguageSelect(lang.code)}
                    disabled={isTranslating}
                  >
                    <span className={styles.dropdownItemFlag}>{lang.flag}</span>
                    <span className={styles.dropdownItemName}>{lang.name}</span>
                    {currentLanguage === lang.code && (
                      <CheckCircle size={16} className={styles.dropdownItemCheck} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Profile Dropdown */}
          {isAuthenticated && (
            <div className={styles.profileDropdown} ref={profileDropdownRef}>
              <button 
                className={styles.profileToggleBtn}
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                aria-label="Profile menu"
              >
                <div className={styles.profileAvatar}>
                  {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className={styles.profileInfo}>
                  <span className={styles.profileName}>{user?.name || user?.email || 'User'}</span>
                  <span className={styles.profileRole}>
                    {isAdmin ? 'Admin' : isProvider ? 'Provider' : 'Client'}
                  </span>
                </div>
                <ChevronDown size={16} className={isProfileDropdownOpen ? styles.rotated : ''} />
              </button>
              {isProfileDropdownOpen && (
                <div className={`${styles.dropdownMenu} ${styles.profileMenu}`}>
                  <div className={styles.dropdownHeader}>
                    <div className={styles.dropdownAvatar}>
                      {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className={styles.dropdownUserInfo}>
                      <div className={styles.dropdownName}>{user?.name || 'User'}</div>
                      <div className={styles.dropdownEmail}>{user?.email || ''}</div>
                      <span className={styles.dropdownRole}>
                        {isAdmin ? 'Administrator' : isProvider ? 'Service Provider' : 'Client'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.dropdownDivider} />
                  {!isAdmin && !isProvider && (
                    <>
                      <Link to="/profile" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                        <User size={16} />
                        <span>{profile}</span>
                      </Link>
                      <Link to="/settings" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                        <Settings size={16} />
                        <span>{settings}</span>
                      </Link>
                    </>
                  )}
                  {isProvider && (
                    <>
                      <Link to="/provider-gallery" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                        <Images size={16} />
                        <span>{gallery}</span>
                      </Link>
                      <Link to="/provider-profile" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                        <User size={16} />
                        <span>{profile}</span>
                      </Link>
                      <Link to="/provider-settings" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                        <Settings size={16} />
                        <span>{settings}</span>
                      </Link>
                    </>
                  )}
                  <div className={styles.dropdownDivider} />
                  <Link to="/help-support" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                    <HelpCircle size={16} />
                    <span>{helpSupport}</span>
                  </Link>
                  <div className={styles.dropdownDivider} />
                  <span className={styles.dropdownLabel}>{legal}</span>
                  <Link to="/terms" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                    <FileText size={16} />
                    <span>{terms}</span>
                  </Link>
                  <Link to="/privacy" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)}>
                    <Shield size={16} />
                    <span>{privacy}</span>
                  </Link>
                  <div className={styles.dropdownDivider} />
                  <button className={`${styles.dropdownItem} ${styles.logoutItem}`} onClick={handleLogout}>
                    <LogOut size={16} />
                    <span>{logoutLabel}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className={styles.mobileMenuToggle}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.open : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.open : ''}`} ref={mobileMenuRef}>
        <div className={styles.mobileMenuContent}>
          {isAuthenticated ? (
            <>
              <div className={styles.mobileUserInfo}>
                <div className={styles.mobileAvatar}>
                  {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className={styles.mobileUserDetails}>
                  <div className={styles.mobileUserName}>{user?.name || 'User'}</div>
                  <div className={styles.mobileUserEmail}>{user?.email || ''}</div>
                  <div className={styles.mobileUserRole}>
                    {isAdmin ? '👑 Administrator' : isProvider ? '🔧 Provider' : '👤 Client'}
                  </div>
                </div>
              </div>

              <div className={styles.mobileNavSection}>
                <span className={styles.mobileSectionLabel}>Main Navigation</span>
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className={`${styles.mobileNavLink} ${isActive(item.path) ? styles.active : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                      {isActive(item.path) && <ChevronRight size={16} className={styles.mobileNavActiveIcon} />}
                    </Link>
                  );
                })}
              </div>

              <div className={styles.mobileDivider} />

              <div className={styles.mobileNavSection}>
                <span className={styles.mobileSectionLabel}>Quick Links</span>
                {quickLinksItems.slice(3).map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className={`${styles.mobileNavLink} ${isActive(item.path) ? styles.active : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              <div className={styles.mobileDivider} />

              <div className={styles.mobileNavSection}>
                <span className={styles.mobileSectionLabel}>{legal}</span>
                {legalItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className={`${styles.mobileNavLink} ${isActive(item.path) ? styles.active : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              <div className={styles.mobileDivider} />

              <div className={styles.mobileSettingsSection}>
                {/* Theme Toggle */}
                <button className={styles.mobileNavLink} onClick={toggleTheme}>
                  {isDark ? <Sun size={20} /> : <Moon size={20} />}
                  <span>{isDark ? lightMode : darkMode}</span>
                </button>

                {/* Language Section */}
                <div className={styles.mobileLanguageSection}>
                  <span className={styles.mobileLanguageLabel}>{language}</span>
                  <div className={styles.mobileLanguageOptions}>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`${styles.mobileLangBtn} ${currentLanguage === lang.code ? styles.active : ''}`}
                        onClick={() => {
                          handleLanguageSelect(lang.code);
                          setIsMobileMenuOpen(false);
                        }}
                        disabled={isTranslating}
                      >
                        <span className={styles.mobileLangFlag}>{lang.flag}</span>
                        <span className={styles.mobileLangName}>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.mobileDivider} />

              {/* Logout Button */}
              <button className={`${styles.mobileNavLink} ${styles.logoutLink}`} onClick={handleLogout}>
                <LogOut size={20} />
                <span>{logoutLabel}</span>
              </button>
            </>
          ) : (
            <>
              <div className={styles.mobileAuthSection}>
                <Link 
                  to="/login" 
                  className={`${styles.mobileNavLink} ${location.pathname === '/login' ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>{signIn}</span>
                </Link>
                <Link 
                  to="/register" 
                  className={`${styles.mobileNavLink} ${styles.registerLink} ${location.pathname === '/register' ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>{register}</span>
                </Link>
              </div>

              <div className={styles.mobileDivider} />

              <div className={styles.mobileNavSection}>
                <span className={styles.mobileSectionLabel}>Quick Links</span>
                {quickLinksItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className={`${styles.mobileNavLink} ${isActive(item.path) ? styles.active : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              <div className={styles.mobileDivider} />

              <div className={styles.mobileSettingsSection}>
                <button className={styles.mobileNavLink} onClick={toggleTheme}>
                  {isDark ? <Sun size={20} /> : <Moon size={20} />}
                  <span>{isDark ? lightMode : darkMode}</span>
                </button>

                <div className={styles.mobileLanguageSection}>
                  <span className={styles.mobileLanguageLabel}>{language}</span>
                  <div className={styles.mobileLanguageOptions}>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`${styles.mobileLangBtn} ${currentLanguage === lang.code ? styles.active : ''}`}
                        onClick={() => {
                          handleLanguageSelect(lang.code);
                          setIsMobileMenuOpen(false);
                        }}
                        disabled={isTranslating}
                      >
                        <span className={styles.mobileLangFlag}>{lang.flag}</span>
                        <span className={styles.mobileLangName}>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavbarComponent;